import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Euro,
  Users,
  Calendar,
  CreditCard,
  Banknote,
  UserPlus,
  UserCheck,
  BarChart3,
  Scissors,
  Clock,
  ArrowUp,
  ArrowDown,
  Minus
} from "lucide-react";
import { format } from "date-fns";
import { it } from "date-fns/locale";

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

export default function TrendAvanzati() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("mese");
  const [data, setData] = useState(null);

  const periodi = [
    { value: "oggi", label: "Oggi" },
    { value: "ieri", label: "Ieri" },
    { value: "settimana", label: "Settimana" },
    { value: "2settimane", label: "2 Settimane" },
    { value: "mese", label: "Mese" },
    { value: "3mesi", label: "3 Mesi" },
    { value: "6mesi", label: "6 Mesi" },
    { value: "anno", label: "Anno" },
  ];

  useEffect(() => {
    fetchData();
  }, [periodo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/trend-avanzati?periodo=${periodo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei trend");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => `€${value.toLocaleString('it-IT', { minimumFractionDigits: 0 })}`;
  
  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM", { locale: it });
    } catch {
      return dateStr;
    }
  };

  const VariazioneIndicator = ({ valore, suffix = "%" }) => {
    if (valore === 0) return <span className="text-slate-400 flex items-center gap-1"><Minus className="w-3 h-3" /> 0{suffix}</span>;
    if (valore > 0) return <span className="text-emerald-600 flex items-center gap-1"><ArrowUp className="w-3 h-3" /> +{valore}{suffix}</span>;
    return <span className="text-red-500 flex items-center gap-1"><ArrowDown className="w-3 h-3" /> {valore}{suffix}</span>;
  };

  // Prepara dati memoizzati - sempre chiamati, anche se data è null
  const metodiPagamentoData = useMemo(() => {
    if (!data?.metodi_pagamento) return [];
    return [
      { name: "Contanti", value: data.metodi_pagamento.contanti, color: "#10b981" },
      { name: "Carta/POS", value: data.metodi_pagamento.carta, color: "#3b82f6" },
      { name: "Card Prep.", value: data.metodi_pagamento.card_prepagata, color: "#8b5cf6" },
      { name: "Altro", value: data.metodi_pagamento.altro, color: "#94a3b8" },
    ].filter(m => m.value > 0);
  }, [data?.metodi_pagamento]);

  const clientiData = useMemo(() => {
    if (!data?.clienti) return [];
    return [
      { name: "Nuovi", value: data.clienti.nuovi, color: "#10b981" },
      { name: "Ritorno", value: data.clienti.ritorno, color: "#3b82f6" },
    ].filter(c => c.value > 0);
  }, [data?.clienti]);

  const operatoriAttivi = useMemo(() => {
    if (!data?.operatori) return [];
    return data.operatori.filter(op => op.incasso > 0 || op.appuntamenti > 0);
  }, [data?.operatori]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data) return null;

  const { riepilogo, clienti, trend_giornaliero, trend_settimana, top_servizi, operatori } = data;

  return (
    <div className="space-y-6">
      {/* Header con filtro periodo */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Analisi Trend
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Periodo attivo: <span className="font-medium text-slate-700">
              {format(new Date(data.periodo.inizio), "dd MMM", { locale: it })} → {format(new Date(data.periodo.fine), "dd MMM yyyy", { locale: it })}
            </span>
            <span className="ml-2 text-slate-400">({data.periodo.giorni} giorni)</span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Quick shortcuts */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {[
              { v: "oggi", l: "Oggi" },
              { v: "settimana", l: "7g" },
              { v: "mese", l: "Mese" },
              { v: "3mesi", l: "3M" },
              { v: "anno", l: "Anno" }
            ].map(p => (
              <button
                key={p.v}
                onClick={() => setPeriodo(p.v)}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                  periodo === p.v
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-white hover:text-slate-900'
                }`}
                data-testid={`quick-period-${p.v}`}
              >
                {p.l}
              </button>
            ))}
          </div>
          {/* Select con tutte le opzioni */}
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-36" data-testid="periodo-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {periodi.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Incasso */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-blue-100 text-sm">Incasso Totale</p>
                <p className="text-3xl font-black mt-1">{formatCurrency(riepilogo.incasso_totale)}</p>
                <div className="mt-2 text-sm">
                  <VariazioneIndicator valore={riepilogo.variazione_incasso} />
                </div>
              </div>
              <Euro className="w-10 h-10 text-blue-200" />
            </div>
            <p className="text-xs text-blue-200 mt-2">vs periodo prec: {formatCurrency(riepilogo.incasso_precedente)}</p>
          </CardContent>
        </Card>

        {/* Appuntamenti */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Appuntamenti</p>
                <p className="text-3xl font-black mt-1">{riepilogo.appuntamenti}</p>
                <div className="mt-2 text-sm">
                  <VariazioneIndicator valore={riepilogo.variazione_appuntamenti} />
                </div>
              </div>
              <Calendar className="w-10 h-10 text-emerald-200" />
            </div>
            <p className="text-xs text-emerald-200 mt-2">vs periodo prec: {riepilogo.appuntamenti_precedente}</p>
          </CardContent>
        </Card>

        {/* Fiche Media */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-purple-100 text-sm">Fiche Media</p>
                <p className="text-3xl font-black mt-1">{formatCurrency(riepilogo.fiche_media)}</p>
                <div className="mt-2 text-sm">
                  <VariazioneIndicator valore={riepilogo.variazione_fiche} suffix="€" />
                </div>
              </div>
              <CreditCard className="w-10 h-10 text-purple-200" />
            </div>
            <p className="text-xs text-purple-200 mt-2">vs periodo prec: {formatCurrency(riepilogo.fiche_media_precedente)}</p>
          </CardContent>
        </Card>

        {/* Clienti */}
        <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-amber-100 text-sm">Clienti Serviti</p>
                <p className="text-3xl font-black mt-1">{clienti.totale_serviti}</p>
                <div className="mt-2 text-sm flex gap-3">
                  <span className="flex items-center gap-1">
                    <UserPlus className="w-3 h-3" /> {clienti.nuovi} nuovi
                  </span>
                </div>
              </div>
              <Users className="w-10 h-10 text-amber-200" />
            </div>
            <p className="text-xs text-amber-200 mt-2">{clienti.ritorno} clienti di ritorno</p>
          </CardContent>
        </Card>
      </div>

      {/* ─────────── KPI Breakdown: Uomo · Donna · Rivendita ─────────── */}
      {data.breakdown && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Uomo */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-sky-500 to-blue-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-sky-100 uppercase tracking-wide">Uomo 👨</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(data.breakdown.uomo.incasso)}</p>
                  <p className="text-xs text-sky-100 mt-1">
                    {data.breakdown.uomo.persone} persone servite
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                  👨
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="bg-white/20 rounded px-2 py-1">
                  Prec: {formatCurrency(data.breakdown.uomo.incasso_precedente)}
                </span>
                <span className={`rounded px-2 py-1 font-semibold ${
                  data.breakdown.uomo.variazione_incasso >= 0 ? 'bg-emerald-400/40' : 'bg-red-400/40'
                }`}>
                  {data.breakdown.uomo.variazione_incasso >= 0 ? '↑' : '↓'} {Math.abs(data.breakdown.uomo.variazione_incasso)}%
                </span>
              </div>
            </CardContent>
          </Card>
          
          {/* Donna */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-pink-500 to-rose-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-pink-100 uppercase tracking-wide">Donna 👩</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(data.breakdown.donna.incasso)}</p>
                  <p className="text-xs text-pink-100 mt-1">
                    {data.breakdown.donna.persone} persone servite
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                  👩
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="bg-white/20 rounded px-2 py-1">
                  Prec: {formatCurrency(data.breakdown.donna.incasso_precedente)}
                </span>
                <span className={`rounded px-2 py-1 font-semibold ${
                  data.breakdown.donna.variazione_incasso >= 0 ? 'bg-emerald-400/40' : 'bg-red-400/40'
                }`}>
                  {data.breakdown.donna.variazione_incasso >= 0 ? '↑' : '↓'} {Math.abs(data.breakdown.donna.variazione_incasso)}%
                </span>
              </div>
            </CardContent>
          </Card>
          
          {/* Rivendita */}
          <Card className="border-0 shadow-md bg-gradient-to-br from-teal-500 to-emerald-600 text-white">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-teal-100 uppercase tracking-wide">Rivendita 🛍️</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(data.breakdown.rivendita.incasso)}</p>
                  <p className="text-xs text-teal-100 mt-1">
                    {data.breakdown.rivendita.pezzi} pezzi venduti
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-2xl">
                  🛍️
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2 text-xs">
                <span className="bg-white/20 rounded px-2 py-1">
                  Prec: {formatCurrency(data.breakdown.rivendita.incasso_precedente)}
                </span>
                <span className={`rounded px-2 py-1 font-semibold ${
                  data.breakdown.rivendita.variazione_incasso >= 0 ? 'bg-emerald-400/40' : 'bg-red-400/40'
                }`}>
                  {data.breakdown.rivendita.variazione_incasso >= 0 ? '↑' : '↓'} {Math.abs(data.breakdown.rivendita.variazione_incasso)}%
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ─────────── Grafico Mensile: Uomo · Donna · Rivendita ─────────── */}
      {data.breakdown_mensile && data.breakdown_mensile.length >= 2 && (
        <Card className="border-0 shadow-lg rounded-2xl" data-testid="chart-mensile-breakdown">
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Confronto Mensile — Uomo · Donna · Rivendita
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Aiuta a capire quali mesi rendono di più per pianificare promo mirate
                </p>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-sky-500"></span>Uomo</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-pink-500"></span>Donna</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-500"></span>Rivendita</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={data.breakdown_mensile} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" tickFormatter={(v) => `€${v}`} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 12 }}
                  formatter={(value, name) => [`€${value}`, name]}
                  labelFormatter={(label) => `Mese: ${label}`}
                />
                <Bar dataKey="uomo" fill="#0ea5e9" name="Uomo" radius={[6,6,0,0]} />
                <Bar dataKey="donna" fill="#ec4899" name="Donna" radius={[6,6,0,0]} />
                <Bar dataKey="rivendita" fill="#14b8a6" name="Rivendita" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
            {/* Riepilogo insight */}
            {(() => {
              const mesi = data.breakdown_mensile;
              const bestUomo = mesi.reduce((a,b) => a.uomo > b.uomo ? a : b, mesi[0]);
              const bestDonna = mesi.reduce((a,b) => a.donna > b.donna ? a : b, mesi[0]);
              const bestRivendita = mesi.reduce((a,b) => a.rivendita > b.rivendita ? a : b, mesi[0]);
              return (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                  <div className="bg-sky-50 rounded-lg p-2 border border-sky-100">
                    <p className="text-slate-500 mb-0.5">🏆 Miglior mese Uomo</p>
                    <p className="font-bold text-sky-700">{bestUomo.label} · €{bestUomo.uomo}</p>
                  </div>
                  <div className="bg-pink-50 rounded-lg p-2 border border-pink-100">
                    <p className="text-slate-500 mb-0.5">🏆 Miglior mese Donna</p>
                    <p className="font-bold text-pink-700">{bestDonna.label} · €{bestDonna.donna}</p>
                  </div>
                  <div className="bg-teal-50 rounded-lg p-2 border border-teal-100">
                    <p className="text-slate-500 mb-0.5">🏆 Miglior mese Rivendita</p>
                    <p className="font-bold text-teal-700">{bestRivendita.label} · €{bestRivendita.rivendita}</p>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* ─────────── Confronto Periodo (M/M, stesso periodo prec.) ─────────── */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl" data-testid="confronto-periodo-card">
        <CardHeader className="pb-2 border-b border-slate-200/60">
          <CardTitle className="text-lg flex items-center gap-2 text-slate-800">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Confronto vs Periodo Precedente
          </CardTitle>
          <p className="text-xs text-slate-500">
            Delta calcolati sullo stesso lasso di tempo antecedente ({data.periodo.giorni} giorni prima)
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Delta Incasso */}
            <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Incasso</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-bold text-slate-800">{formatCurrency(riepilogo.incasso_totale)}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">Prec: {formatCurrency(riepilogo.incasso_precedente)}</p>
              <div className="mt-1"><VariazioneIndicator valore={riepilogo.variazione_incasso} suffix="%" /></div>
            </div>
            {/* Delta Appuntamenti */}
            <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Appuntamenti</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-bold text-slate-800">{riepilogo.appuntamenti}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">Prec: {riepilogo.appuntamenti_precedente}</p>
              <div className="mt-1"><VariazioneIndicator valore={riepilogo.variazione_appuntamenti} suffix="%" /></div>
            </div>
            {/* Delta Fiche Media */}
            <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Fiche Media</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-bold text-slate-800">{formatCurrency(riepilogo.fiche_media)}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">Prec: {formatCurrency(riepilogo.fiche_media_precedente)}</p>
              <div className="mt-1"><VariazioneIndicator valore={riepilogo.variazione_fiche} suffix="€" /></div>
            </div>
            {/* Delta Clienti Serviti */}
            <div className="bg-white rounded-xl p-3 border border-slate-200 shadow-sm">
              <p className="text-[10px] text-slate-500 uppercase tracking-wide">Clienti Serviti</p>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-lg font-bold text-slate-800">{clienti.totale_serviti}</span>
              </div>
              <p className="text-[10px] text-slate-400 mt-0.5">
                Nuovi <span className="font-semibold text-emerald-600">{clienti.nuovi}</span> · Ritorno <span className="font-semibold text-blue-600">{clienti.ritorno}</span>
              </p>
            </div>
          </div>
          {/* Riga insights */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <div className="bg-white/70 rounded-lg p-2 border border-slate-200/60">
              <p className="text-slate-500 mb-0.5">💡 Fiche/cliente</p>
              <p className="font-bold text-slate-800">
                {clienti.totale_serviti > 0 ? formatCurrency(riepilogo.incasso_totale / clienti.totale_serviti) : '—'}
              </p>
            </div>
            <div className="bg-white/70 rounded-lg p-2 border border-slate-200/60">
              <p className="text-slate-500 mb-0.5">📅 Media/giorno</p>
              <p className="font-bold text-slate-800">
                {data.periodo.giorni > 0 ? formatCurrency(riepilogo.incasso_totale / data.periodo.giorni) : '—'}
              </p>
            </div>
            <div className="bg-white/70 rounded-lg p-2 border border-slate-200/60">
              <p className="text-slate-500 mb-0.5">🎯 % Ritorno</p>
              <p className="font-bold text-slate-800">
                {clienti.totale_serviti > 0 ? `${Math.round((clienti.ritorno / clienti.totale_serviti) * 100)}%` : '—'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Grafici Row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Trend Giornaliero */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Andamento Incassi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {trend_giornaliero.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trend_giornaliero}>
                  <defs>
                    <linearGradient id="colorIncasso" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="data" 
                    tickFormatter={formatDate}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <YAxis 
                    tickFormatter={(v) => `€${v}`}
                    tick={{ fontSize: 11 }}
                    stroke="#94a3b8"
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), "Incasso"]}
                    labelFormatter={(label) => format(new Date(label), "EEEE dd/MM", { locale: it })}
                    contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="incasso" 
                    stroke="#3b82f6" 
                    strokeWidth={2}
                    fillOpacity={1} 
                    fill="url(#colorIncasso)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-slate-400">
                Nessun dato disponibile
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trend per Giorno Settimana */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              Incassi per Giorno
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={trend_settimana}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="giorno" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <YAxis tickFormatter={(v) => `€${v}`} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                <Tooltip 
                  formatter={(value) => [formatCurrency(value), "Incasso"]}
                  contentStyle={{ borderRadius: 8, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                />
                <Bar dataKey="incasso" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Grafici Row 2 */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Top Servizi */}
        <Card className="border-0 shadow-lg lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Scissors className="w-5 h-5 text-purple-600" />
              Top Servizi
            </CardTitle>
          </CardHeader>
          <CardContent>
            {top_servizi.length > 0 ? (
              <div className="space-y-3">
                {top_servizi.slice(0, 8).map((servizio, idx) => {
                  const maxIncasso = top_servizi[0]?.incasso || 1;
                  const pct = (servizio.incasso / maxIncasso) * 100;
                  return (
                    <div key={`servizio-${servizio.nome}-${idx}`} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium text-slate-700 flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}>
                            {idx + 1}
                          </span>
                          {servizio.nome}
                        </span>
                        <span className="text-slate-500">
                          <span className="font-bold text-slate-800">{formatCurrency(servizio.incasso)}</span>
                          <span className="text-xs ml-2">({servizio.count}x)</span>
                        </span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400">
                Nessun servizio nel periodo
              </div>
            )}
          </CardContent>
        </Card>

        {/* Metodi Pagamento */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Metodi Pagamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metodiPagamentoData.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie
                      data={metodiPagamentoData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {metodiPagamentoData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {metodiPagamentoData.map((m) => (
                    <div key={m.name} className="flex items-center gap-2 text-xs">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }}></div>
                      <span className="text-slate-600">{m.name}</span>
                      <span className="font-bold ml-auto">{formatCurrency(m.value)}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-40 flex items-center justify-center text-slate-400">
                Nessun pagamento
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Performance Operatori */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-blue-600" />
            Performance Operatori
          </CardTitle>
        </CardHeader>
        <CardContent>
          {operatori.length > 0 ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {operatoriAttivi.map((op) => {
                const maxIncasso = operatori[0]?.incasso || 1;
                const pct = maxIncasso > 0 ? (op.incasso / maxIncasso) * 100 : 0;
                return (
                  <div 
                    key={op.id}
                    className="p-4 bg-slate-50 rounded-xl hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div 
                        className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: op.colore }}
                      >
                        {op.nome.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-slate-800 truncate">{op.nome}</p>
                        <p className="text-xs text-slate-500">{op.appuntamenti} appuntamenti</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-2xl font-black text-slate-800">{formatCurrency(op.incasso)}</span>
                      </div>
                      <Progress value={pct} className="h-1.5" />
                    </div>
                  </div>
                );
              })}
              {operatoriAttivi.length === 0 && (
                <div className="col-span-full text-center py-8 text-slate-400">
                  Nessun operatore con attività nel periodo
                </div>
              )}
            </div>
          ) : (
            <div className="h-40 flex items-center justify-center text-slate-400">
              Nessun operatore configurato
            </div>
          )}
        </CardContent>
      </Card>

      {/* Clienti Nuovi vs Ritorno */}
      {(clienti.nuovi > 0 || clienti.ritorno > 0) && (
        <Card className="border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-amber-600" />
              Clienti: Nuovi vs Ritorno
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-8">
              <ResponsiveContainer width={150} height={150}>
                <PieChart>
                  <Pie
                    data={clientiData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={60}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {clientiData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <UserPlus className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-slate-800">{clienti.nuovi}</p>
                    <p className="text-sm text-slate-500">Clienti Nuovi</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-black text-slate-800">{clienti.ritorno}</p>
                    <p className="text-sm text-slate-500">Clienti di Ritorno</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
